import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  computed,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';

import { NavigationService } from '../../core/services/navigation.service';
import { flattenRoutes, normalizeText, SearchRoute } from './internal-search.model';

/** px value matching $breakpoint-md in _variables.scss */
const MD_BREAKPOINT = 768;

@Component({
  selector: 'app-internal-search',
  standalone: true,
  imports: [ReactiveFormsModule, NgTemplateOutlet],
  templateUrl: './internal-search.component.html',
  styleUrl: './internal-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalSearchComponent {
  private readonly router = inject(Router);
  private readonly navService = inject(NavigationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);

  /** Desktop inline input reference (always in DOM, hidden on mobile via CSS). */
  protected readonly desktopInputRef = viewChild<ElementRef<HTMLInputElement>>('inputRef');
  /** Mobile overlay input reference (in DOM only while overlay is open). */
  protected readonly mobileInputRef = viewChild<ElementRef<HTMLInputElement>>('mobileInputRef');

  protected readonly searchControl = new FormControl('', { nonNullable: true });

  /** Debounced query value driving the `results` computation. */
  protected readonly query = signal('');

  /** Whether the results dropdown is visible. */
  protected readonly isOpen = signal(false);

  /** Whether the mobile full-screen overlay is open. */
  protected readonly isMobileOverlayOpen = signal(false);

  /** Index of the keyboard-highlighted result (-1 = none). */
  protected readonly activeIndex = signal(-1);

  /** All navigable leaf routes derived from the filtered menu tree. */
  private readonly allRoutes = computed(() => flattenRoutes(this.navService.filteredMenu()));

  /**
   * Routes matching the current query.
   * Requires at least 2 characters to avoid overwhelming results.
   */
  protected readonly results = computed((): SearchRoute[] => {
    const q = normalizeText(this.query());
    if (q.length < 2) {
      return [];
    }
    return this.allRoutes().filter(
      (r) => normalizeText(r.label).includes(q) || normalizeText(r.breadcrumb).includes(q),
    );
  });

  constructor() {
    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.query.set(value);
        this.activeIndex.set(-1);
        this.isOpen.set(value.trim().length >= 2);
      });
  }

  /** Close the dropdown when clicking outside this component (desktop only path). */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: Event): void {
    if (!this.isMobileOverlayOpen() && !this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  /** Ctrl+K / Cmd+K opens search from anywhere. */
  @HostListener('document:keydown', ['$event'])
  protected onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      if (window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`).matches) {
        this.openMobileOverlay();
      } else {
        this.desktopInputRef()?.nativeElement.focus();
      }
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    const res = this.results();

    switch (event.key) {
      case 'Escape':
        if (this.isMobileOverlayOpen()) {
          this.closeMobileOverlay();
        } else {
          this.closeDropdown();
        }
        event.preventDefault();
        break;

      case 'ArrowDown':
        if (!this.isOpen() && res.length) {
          this.isOpen.set(true);
        }
        this.activeIndex.set(Math.min(this.activeIndex() + 1, res.length - 1));
        event.preventDefault();
        break;

      case 'ArrowUp':
        this.activeIndex.set(Math.max(this.activeIndex() - 1, -1));
        event.preventDefault();
        break;

      case 'Enter':
        if (this.activeIndex() >= 0) {
          const selected = res[this.activeIndex()];
          if (selected) {
            this.navigate(selected);
            event.preventDefault();
          }
        }
        break;
    }
  }

  protected openMobileOverlay(): void {
    this.isMobileOverlayOpen.set(true);
    document.body.style.overflow = 'hidden';
    // Focus input after @if renders on next microtask
    setTimeout(() => this.mobileInputRef()?.nativeElement.focus(), 0);
  }

  protected closeMobileOverlay(): void {
    this.isMobileOverlayOpen.set(false);
    document.body.style.overflow = '';
    this.closeDropdown();
    this.searchControl.setValue('');
    this.query.set('');
  }

  protected navigate(result: SearchRoute): void {
    this.router.navigate([result.route]);
    if (this.isMobileOverlayOpen()) {
      this.closeMobileOverlay();
    } else {
      this.closeDropdown();
    }
  }

  protected clear(): void {
    this.searchControl.setValue('');
    this.query.set('');
    this.closeDropdown();
    const active = this.mobileInputRef() ?? this.desktopInputRef();
    active?.nativeElement.focus();
  }

  private closeDropdown(): void {
    this.isOpen.set(false);
    this.activeIndex.set(-1);
  }
}
